/**
 * Inline "Create an Idea" panel inside the chat input box.
 *
 * Step 1 — list of options shown; header reads "Create an idea based on…"
 *           send button is DISABLED.
 * Step 2 — user clicks an option row; header fills in:
 *           "Create an idea based on [all insights in the session]"
 *           send button turns ORANGE and becomes clickable.
 * Confirm — clicking the orange button calls onConfirm(selectedOption).
 * Dismiss — [✕] or Escape calls onDismiss().
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  💡  Create an idea based on [all insights in the session] [→][✕]│
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │  ● all insights in the session                                   │
 *   │    the answer to "query 1"                                       │
 *   │    the answer to "query 2"                                       │
 *   └──────────────────────────────────────────────────────────────────┘
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Flex, ScrollArea, Text } from '@mantine/core';
import { IconArrowUp, IconBulb, IconX } from '@tabler/icons';
import type { CreateIdeaOption } from './createIdeaFromChatPrompt';

export interface CreateIdeaInputPromptProps {
  options: CreateIdeaOption[];
  onConfirm: (option: CreateIdeaOption) => void;
  onDismiss: () => void;
  disabled?: boolean;
}

export const CreateIdeaInputPrompt: React.FC<CreateIdeaInputPromptProps> = ({
  options,
  onConfirm,
  onDismiss,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedOption, setSelectedOption] = useState<CreateIdeaOption | null>(null);

  // Focus on mount so Escape works immediately
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
      if (e.key === 'Enter' && !e.shiftKey && selectedOption && !disabled) {
        e.preventDefault();
        onConfirm(selectedOption);
      }
    },
    [onDismiss, onConfirm, selectedOption, disabled]
  );

  if (!options.length) return null;

  const canConfirm = !!selectedOption && !disabled;

  // Truncate label for the header chip
  const chipLabel = selectedOption
    ? selectedOption.label.length > 50
      ? selectedOption.label.slice(0, 47) + '…'
      : selectedOption.label
    : null;

  return (
    <Box
      w="100%"
      bg="rgba(248, 248, 251, 1)"
      sx={{ minWidth: 0, boxSizing: 'border-box', overflowX: 'hidden' }}
    >
      <Box
        ref={containerRef as any}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          backgroundColor: 'white',
          border: '1.5px solid #5C5CEB',
          borderRadius: '8px',
          overflow: 'hidden',
          outline: 'none',
          marginTop: '12px',
          '&:focus-visible': { boxShadow: '0 0 0 2px #5C5CEB33' },
        }}
      >
        {/* ── Header row ── */}
        <Flex
          align="center"
          px={12}
          py={10}
          sx={{ gap: 6, borderBottom: '1px solid #EBEBF5' }}
        >
          <IconBulb size={15} color="#5C5CEB" style={{ flexShrink: 0 }} />

          {/* Prompt text + selected option chip */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" color="#212529" sx={{ lineHeight: 1.4 }}>
            Create a one-pager from insights in... {' '}
              {chipLabel && (
                <Text component="span" fw={600} color="#5C5CEB">
                  [{chipLabel}]
                </Text>
              )}
            </Text>
          </Box>

          {/* Send button — orange only when an option is selected */}
          <ActionIcon
            size={28}
            onClick={canConfirm ? () => onConfirm(selectedOption!) : undefined}
            disabled={!canConfirm}
            sx={{
              flexShrink: 0,
              borderRadius: '6px',
              backgroundColor: canConfirm ? '#FF8700' : '#E9ECEF',
              transition: 'background-color 0.15s ease',
              '&:hover': { backgroundColor: canConfirm ? '#e07900' : '#E9ECEF' },
              '&:disabled': { backgroundColor: '#E9ECEF', cursor: 'not-allowed' },
            }}
            title={canConfirm ? 'Create idea (Enter)' : 'Select an option first'}
          >
            <IconArrowUp size={15} stroke={2.5} color={canConfirm ? '#FFFFFF' : '#ADB5BD'} />
          </ActionIcon>

          {/* Dismiss */}
          <ActionIcon
            size={22}
            variant="subtle"
            color="gray"
            onClick={onDismiss}
            title="Dismiss (Esc)"
            sx={{ flexShrink: 0 }}
          >
            <IconX size={13} />
          </ActionIcon>
        </Flex>

        {/* ── Options list — max 4 visible; scroll reveals the rest ── */}
        <ScrollArea.Autosize mah={160} type="auto">
          {options.map((option, i) => {
            const isSelected = selectedOption?.payload === option.payload;
            const labelFull = option.label;
            const labelDisplay = labelFull.length > 110 ? labelFull.slice(0, 107) + '…' : labelFull;

            return (
              <Flex
                key={option.payload}
                align="center"
                px={12}
                py={9}
                onClick={() => setSelectedOption(option)}
                sx={{
                  gap: 10,
                  cursor: 'pointer',
                  borderTop: i > 0 ? '1px solid #F3F3FA' : 'none',
                  backgroundColor: isSelected ? '#F0F0FF' : 'transparent',
                  transition: 'background-color 0.1s ease',
                  '&:hover': {
                    backgroundColor: isSelected ? '#EAEAFF' : '#F5F5FF',
                  },
                }}
              >
                {/* Selection dot */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: isSelected ? '#5C5CEB' : '#D1D5DB',
                    transition: 'background-color 0.1s ease',
                  }}
                />

                <Text
                  size="sm"
                  fw={isSelected ? 500 : 400}
                  color={isSelected ? '#212529' : '#495057'}
                  sx={{ flex: 1, lineHeight: 1.4 }}
                  title={labelFull.length > 62 ? labelFull : undefined}
                >
                  {labelDisplay}
                </Text>
              </Flex>
            );
          })}
        </ScrollArea.Autosize>
      </Box>
    </Box>
  );
};

export default CreateIdeaInputPrompt;
